import React from "react";

// Ensure your priceUtils are imported correctly
import { formatPrice, formatPriceWithLocale, formatTjs } from "../../utils/priceUtils";
import NoTranslate from "./NoTranslate";

interface PriceProps {
  value: number | string | null | undefined;
  currency?: string;
  decimals?: number;
  locale?: string;
  format?: "default" | "locale";
  disableCentsHeuristic?: boolean;
  fallback?: React.ReactNode;
  as?: React.ElementType;
  className?: string;
  tjsAmount?: number | null;
  [key: string]: any; // Allow rest props (style, onClick, etc.)
}

const Price: React.FC<PriceProps> = ({
  value,
  currency = "$",
  decimals = 2,
  locale = "en-US",
  format = "default",
  disableCentsHeuristic = false,
  fallback,
  as = "span",
  className = "",
  tjsAmount,
  ...props
}) => {
  // 1. Robust Validity Check (Decoupled from "N/A" string)
  const numericValue = typeof value === "string" ? parseFloat(value) : value;
  const isValid =
    value !== null && value !== undefined && !isNaN(numericValue as number);

  // 2. Return fallback immediately if invalid
  if (!isValid) {
    // If fallback is provided, render it. Otherwise render "N/A" (or whatever formatPrice returns for null)
    if (fallback !== undefined) {
      return <>{fallback}</>;
    }
  }

  // 3. Formatting Logic
  let formattedDisplay: string;

  if (format === "locale") {
    // Note: Ideally, update formatPriceWithLocale in your utils to accept 'decimals'
    // For now, this calls the util as defined previously.
    formattedDisplay = formatPriceWithLocale(value, currency, locale, {
      disableCentsHeuristic,
    });
  } else {
    formattedDisplay = formatPrice(value, currency, decimals, {
      disableCentsHeuristic,
    });
  }

  const hasTjs =
    tjsAmount !== null &&
    tjsAmount !== undefined &&
    !isNaN(Number(tjsAmount));

  return (
    <NoTranslate as={as} className={className} {...props}>
      {hasTjs ? (
        <>
          <span style={{ fontWeight: 700 }}>
            {formatTjs(Number(tjsAmount))} c.
          </span>
          <span style={{ color: "#888", fontSize: "0.8em", marginLeft: 4 }}>
            ({formattedDisplay})
          </span>
        </>
      ) : (
        formattedDisplay
      )}
    </NoTranslate>
  );
};

export default Price;
